import { HttpResponse } from '@angular/common/http';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { BehaviorSubject, forkJoin, iif, Observable, of, Subscription, throwError } from 'rxjs';
import { mergeMap, skip, take } from 'rxjs/operators';
import { UserInterface } from '../../../models/interface/user.interface';
import { GetAllUsersAdminPayload, UserActionTypes } from '../../../store/user/user.action';
import { UserStore } from '../../../store/user/component-store/user.store';

type Role = 'admin' | 'ecoach' | 'editor';

@Component({
  selector: 'app-dialog-admin-user-update',
  templateUrl: './dialog-admin-user-update.component.html',
  styleUrls: ['./dialog-admin-user-update.component.scss'],
  providers: [UserStore],
})
export class DialogAdminUserUpdateComponent implements OnInit, OnDestroy {
  public user: UserInterface;

  public updateAccountResponse = new BehaviorSubject<'DEFAULT' | 'LOADING' | 'SUCCESS' | 'FAILURE'>('DEFAULT');

  public adminChecked = false;
  public ecoachChecked = false;
  public editorChecked = false;

  private users$: Observable<UserInterface[]>;

  private assignRolesToUserAdminResponse$: Observable<any>;
  private removeRolesFromUserAdminResponse$: Observable<any>;

  private subscriptions: Subscription[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private userStore: UserStore,
    private store: Store<{ users: UserInterface[] }>,
    private dialogRef: MatDialogRef<DialogAdminUserUpdateComponent>,
  ) {
    this.users$ = this.store.select('users');
    this.assignRolesToUserAdminResponse$ = this.userStore.assignRolesToUserAdminResponse$;
    this.removeRolesFromUserAdminResponse$ = this.userStore.removeRolesFromUserAdminResponse$;
  }

  ngOnInit(): void {
    this.user = this.data.user;
    this.adminChecked = this.isUserInRole(this.user, 'admin');
    this.ecoachChecked = this.isUserInRole(this.user, 'ecoach');
    this.editorChecked = this.isUserInRole(this.user, 'editor');
  }

  public isUserInRole(user: UserInterface, slug: Role): boolean {
    const globalRoles = user.attributes.roles;
    return globalRoles.some((role) => role.slug.toString() === slug);
  }

  public updateAccount(): void {
    if (this.updateAccountResponse.value !== 'LOADING') {
      this.updateAccountResponse.next('LOADING');

      const removingRoles: Role[] = [];
      const addingRoles: Role[] = [];

      if (this.adminChecked) {
        if (!this.isUserInRole(this.user, 'admin')) {
          addingRoles.push('admin');
        }
      } else if (this.isUserInRole(this.user, 'admin')) {
        removingRoles.push('admin');
      }

      if (this.ecoachChecked) {
        if (!this.isUserInRole(this.user, 'ecoach')) {
          addingRoles.push('ecoach');
        }
      } else if (this.isUserInRole(this.user, 'ecoach')) {
        removingRoles.push('ecoach');
      }

      if (this.editorChecked) {
        if (!this.isUserInRole(this.user, 'editor')) {
          addingRoles.push('editor');
        }
      } else if (this.isUserInRole(this.user, 'editor')) {
        removingRoles.push('editor');
      }

      const reqs: Array<Observable<HttpResponse<any>>> = [];
      if (removingRoles.length > 0) {
        this.userStore.removeRolesFromUserAdmin({ userId: this.user.id, roleSlugs: removingRoles });
        reqs.push(
          this.removeRolesFromUserAdminResponse$.pipe(
            skip(1),
            take(1),
            mergeMap(
              (result) => iif(
                () => result instanceof HttpResponse,
                of(result),
                throwError(() => result),
              ),
            ),
          ),
        );
      }
      if (addingRoles.length > 0) {
        this.userStore.assignRolesToUserAdmin({ userId: this.user.id, roleSlugs: addingRoles });
        reqs.push(
          this.assignRolesToUserAdminResponse$.pipe(
            skip(1),
            take(1),
            mergeMap(
              (result) => iif(
                () => result instanceof HttpResponse,
                of(result),
                throwError(() => result),
              ),
            ),
          ),
        );
      }
      this.subscriptions.push(
        forkJoin(reqs)
          .pipe(
            mergeMap(() => {
              const payload: GetAllUsersAdminPayload = { userId: this.user.id };
              this.store.dispatch({ type: UserActionTypes.getAllUsersAdminType, payload });
              return this.users$.pipe(skip(1), take(1));
            })
          )
          .subscribe({
            next: (userList) => {
              if (userList.length > 0) {
                this.user = userList[0];
              }
              this.updateAccountResponse.next('SUCCESS');
              setTimeout(() => {
                this.updateAccountResponse.next('DEFAULT');
              }, 2500);
            },
            error: () => {
              this.updateAccountResponse.next('FAILURE');
              setTimeout(() => {
                this.updateAccountResponse.next('DEFAULT');
              }, 2500);
            },
            complete: () => {
              this.updateAccountResponse.next('SUCCESS');
              setTimeout(() => {
                this.updateAccountResponse.next('DEFAULT');
              }, 2500);
            },
          }),
      );
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
